import React from 'react';

const DISC_CONFIG = {
    D: { label: 'Dominant', color: 'bg-rose-100 text-rose-700 border-rose-200', dot: 'bg-rose-500', desc: 'Direct, results-oriented, decisive' },
    I: { label: 'Influential', color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500', desc: 'Enthusiastic, collaborative, social' },
    S: { label: 'Steady', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', desc: 'Patient, reliable, team-oriented' },
    C: { label: 'Conscientious', color: 'bg-sky-100 text-sky-700 border-sky-200', dot: 'bg-sky-500', desc: 'Analytical, precise, systematic' },
};

export default function DISCBadge({ profile, secondary, scores, size = 'sm', className = '' }) {
    if (!profile) return null;

    const config = DISC_CONFIG[profile] || DISC_CONFIG.S;

    // Small: just a colored dot with letter
    if (size === 'sm') {
        return (
            <span
                className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold text-white ${config.dot} ${className}`}
                title={`${config.label}: ${config.desc}`}
            >
                {profile}
            </span>
        );
    }

    // Medium: pill badge with type name
    if (size === 'md') {
        return (
            <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${config.color} ${className}`}
                title={config.desc}
            >
                <span className={`w-2 h-2 rounded-full ${config.dot}`} />
                {secondary || profile}
            </span>
        );
    }

    // Large: full panel with scores
    if (size === 'lg' && scores) {
        const maxScore = Math.max(...Object.values(scores), 1);
        return (
            <div className={`space-y-3 ${className}`}>
                <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-semibold border ${config.color}`}>
                        <span className={`w-2.5 h-2.5 rounded-full ${config.dot}`} />
                        {secondary || profile} — {config.label}
                    </span>
                </div>
                <p className="text-xs text-stone-500">{config.desc}</p>
                <div className="space-y-2">
                    {Object.entries(DISC_CONFIG).map(([key, cfg]) => {
                        const score = scores[key] || 0;
                        return (
                            <div key={key} className="flex items-center gap-2">
                                <span className="text-xs font-medium text-stone-500 w-4">{key}</span>
                                <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full rounded-full transition-all ${cfg.dot}`}
                                        style={{ width: `${(score / 100) * 100}%` }}
                                    />
                                </div>
                                <span className="text-xs text-stone-400 w-6 text-right">{score}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // Fallback to medium
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${config.color} ${className}`}>
            {secondary || profile}
        </span>
    );
}

// Export config for reuse
export { DISC_CONFIG };
